/**
 * StructureSprites - Pixel art sprites for structures
 * Each sprite is a 16x16 grid of colors (null = transparent)
 */

type PixelGrid = (string | null)[][];

// Color palette
const C = {
  // Wood colors
  wood1: '#8B4513',    // Dark brown
  wood2: '#A0522D',    // Medium brown
  wood3: '#CD853F',    // Light brown
  wood4: '#DEB887',    // Very light brown
  woodDark: '#5D3A1A', // Very dark brown

  // Stone colors
  stone1: '#555555',   // Dark gray
  stone2: '#707070',   // Medium gray
  stone3: '#909090',   // Light gray
  stoneDark: '#3A3A3A', // Very dark gray

  // Metal colors
  metal1: '#8B8B8B',   // Dark metal
  metal2: '#A8A8A8',   // Medium metal
  metal3: '#C0C0C0',   // Light metal
  metalGold: '#DAA520', // Gold accent

  // Fire/warm colors
  orange1: '#FF6B00',
  orange2: '#FF8C00',
  red1: '#CC3300',
  yellow1: '#FFD700',

  // Black/shadows
  black: '#1A1A1A',
  shadow: '#2A2A2A',
};

const _ = null; // Transparent

// ============ WOODEN CHEST ============
// A classic treasure chest shape
const chestWood: PixelGrid = [
  [_, _, _, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, _, _, _],
  [_, _, C.wood1, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood1, C.woodDark, _, _],
  [_, C.wood1, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood3, C.wood2, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood3, C.wood2, C.wood3, C.wood2, C.metalGold, C.metalGold, C.metalGold, C.metalGold, C.wood2, C.wood3, C.wood2, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood2, C.wood3, C.wood2, C.wood3, C.metalGold, C.metal3, C.metal3, C.metalGold, C.wood3, C.wood2, C.wood3, C.wood1, C.woodDark, _],
  [_, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metalGold, C.metalGold, C.metalGold, C.metalGold, C.metal2, C.metal2, C.metal2, C.metal2, C.metal1, _],
  [_, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood1, C.woodDark, _],
  [_, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.woodDark, _],
  [_, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal2, C.metal1, _],
  [_, C.woodDark, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.wood1, C.woodDark, C.shadow, _],
  [_, _, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.woodDark, C.shadow, _, _],
  [_, _, _, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// ============ CAMPFIRE BASE ============
// Stone circle with logs - fire effect rendered separately
const campfireBase: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, C.stone2, C.stone2, C.stone3, C.stone3, C.stone2, C.stone2, _, _, _, _, _],
  [_, _, _, C.stone1, C.stone2, C.stoneDark, C.wood1, C.wood2, C.wood1, C.stoneDark, C.stone2, C.stone1, _, _, _, _],
  [_, _, C.stone1, C.stone2, C.stoneDark, C.wood2, C.orange1, C.yellow1, C.orange1, C.wood1, C.stoneDark, C.stone2, C.stone1, _, _, _],
  [_, _, C.stone2, C.stoneDark, C.wood1, C.wood3, C.yellow1, C.orange2, C.yellow1, C.wood3, C.wood2, C.stoneDark, C.stone2, _, _, _],
  [_, _, C.stone2, C.stoneDark, C.wood2, C.orange1, C.orange2, C.red1, C.orange2, C.orange1, C.wood1, C.stoneDark, C.stone2, _, _, _],
  [_, _, C.stone3, C.stoneDark, C.wood1, C.wood3, C.yellow1, C.orange2, C.yellow1, C.wood3, C.wood2, C.stoneDark, C.stone3, _, _, _],
  [_, _, C.stone3, C.stoneDark, C.wood2, C.wood1, C.orange1, C.yellow1, C.orange1, C.wood1, C.wood1, C.stoneDark, C.stone3, _, _, _],
  [_, _, C.stone2, C.stoneDark, C.stoneDark, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.stoneDark, C.stoneDark, C.stone2, _, _, _],
  [_, _, C.stone1, C.stone2, C.stoneDark, C.stoneDark, C.stoneDark, C.stoneDark, C.stoneDark, C.stoneDark, C.stoneDark, C.stone2, C.stone1, _, _, _],
  [_, _, _, C.stone1, C.stone2, C.stone2, C.stone3, C.stone3, C.stone3, C.stone3, C.stone2, C.stone2, C.stone1, _, _, _],
  [_, _, _, _, C.stone1, C.stone1, C.stone2, C.stone2, C.stone2, C.stone2, C.stone1, C.stone1, _, _, _, _],
  [_, _, _, _, _, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, C.shadow, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// ============ WORKBENCH ============
// A sturdy wooden crafting table with tools
const workbench: PixelGrid = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, C.metal2, C.metal1, _, _, _, _, _, _, C.stone2, C.stone1, _, _, _],
  [_, _, _, C.metal2, C.metal1, _, _, _, _, _, _, C.stone2, C.stone1, _, _, _],
  [_, _, C.wood3, C.wood2, C.wood1, C.wood3, C.wood2, C.wood1, C.wood3, C.wood2, C.wood1, C.wood3, C.wood2, C.wood1, _, _],
  [_, C.wood2, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood1, _],
  [_, C.wood1, C.wood3, C.wood4, C.metal2, C.wood4, C.wood3, C.wood4, C.wood3, C.metal2, C.wood4, C.wood3, C.wood4, C.wood2, C.woodDark, _],
  [_, C.wood1, C.wood4, C.wood3, C.metal1, C.wood3, C.wood4, C.wood3, C.wood4, C.metal1, C.wood3, C.wood4, C.wood3, C.wood2, C.woodDark, _],
  [_, C.wood2, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood4, C.wood3, C.wood1, C.woodDark, _],
  [_, C.woodDark, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.wood2, C.wood1, C.woodDark, C.shadow, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _, _, _, C.wood2, C.wood1, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _, _, _, C.wood2, C.wood1, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _, _, _, C.wood2, C.wood1, _, _],
  [_, _, C.wood1, C.wood2, _, _, _, _, _, _, _, _, C.wood2, C.wood1, _, _],
  [_, _, C.woodDark, C.wood1, _, _, _, _, _, _, _, _, C.wood1, C.woodDark, _, _],
  [_, _, C.shadow, C.woodDark, _, _, _, _, _, _, _, _, C.woodDark, C.shadow, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// ============ HELL HOLE ============
// A dark portal to the underworld with red glow
const hell = {
  dark: '#0A0000',
  darker: '#050000',
  red1: '#8B0000',
  red2: '#CC1100',
  red3: '#FF2200',
  orange: '#FF4400',
  glow: '#FF6600',
};

const hellHole: PixelGrid = [
  [_, _, _, _, _, _, hell.red1, hell.red1, hell.red1, hell.red1, _, _, _, _, _, _],
  [_, _, _, _, hell.red1, hell.red2, hell.red3, hell.orange, hell.orange, hell.red3, hell.red2, hell.red1, _, _, _, _],
  [_, _, _, hell.red1, hell.red2, hell.red3, hell.glow, hell.dark, hell.dark, hell.glow, hell.red3, hell.red2, hell.red1, _, _, _],
  [_, _, hell.red1, hell.red2, hell.red3, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red3, hell.red2, hell.red1, _, _],
  [_, _, hell.red1, hell.red3, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red3, hell.red1, _, _],
  [_, hell.red1, hell.red2, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red2, hell.red1, _],
  [hell.red1, hell.red2, hell.glow, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.glow, hell.red2, hell.red1, _],
  [hell.red1, hell.orange, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.orange, hell.red1, _],
  [hell.red1, hell.orange, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.orange, hell.red1, _],
  [hell.red1, hell.red2, hell.glow, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.glow, hell.red2, hell.red1, _],
  [_, hell.red1, hell.red2, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red2, hell.red1, _],
  [_, _, hell.red1, hell.red3, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red3, hell.red1, _, _],
  [_, _, hell.red1, hell.red2, hell.red3, hell.dark, hell.darker, hell.darker, hell.darker, hell.darker, hell.dark, hell.red3, hell.red2, hell.red1, _, _],
  [_, _, _, hell.red1, hell.red2, hell.red3, hell.glow, hell.dark, hell.dark, hell.glow, hell.red3, hell.red2, hell.red1, _, _, _],
  [_, _, _, _, hell.red1, hell.red2, hell.red3, hell.orange, hell.orange, hell.red3, hell.red2, hell.red1, _, _, _, _],
  [_, _, _, _, _, _, hell.red1, hell.red1, hell.red1, hell.red1, _, _, _, _, _, _],
];

// ============ LADDER ============
// A wooden ladder for climbing out of hell
const ladderWood = {
  rail: '#8B4513',
  railLight: '#A0522D',
  rung: '#CD853F',
  rungLight: '#DEB887',
  dark: '#5D3A1A',
};

const ladder: PixelGrid = [
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.rung, ladderWood.rung, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rung, ladderWood.rung, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.rung, ladderWood.rung, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rung, ladderWood.rung, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.rung, ladderWood.rung, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rung, ladderWood.rung, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.rung, ladderWood.rung, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rungLight, ladderWood.rung, ladderWood.rung, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.rail, ladderWood.railLight, _, _, _, _, _, _, ladderWood.railLight, ladderWood.rail, _, _, _],
  [_, _, _, ladderWood.dark, ladderWood.rail, _, _, _, _, _, _, ladderWood.rail, ladderWood.dark, _, _, _],
  [_, _, _, _, ladderWood.dark, _, _, _, _, _, _, ladderWood.dark, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Sprite registry
const STRUCTURE_SPRITES: { [key: string]: PixelGrid } = {
  'chest_wood': chestWood,
  'campfire': campfireBase,
  'workbench': workbench,
  'hell_hole': hellHole,
  'ladder': ladder,
};

/**
 * Check if a structure has a pixel art sprite
 */
export function hasStructureSprite(structureId: string): boolean {
  return structureId in STRUCTURE_SPRITES;
}

/**
 * Render a structure sprite at the given position
 */
export function renderStructureSprite(
  ctx: CanvasRenderingContext2D,
  structureId: string,
  x: number,
  y: number,
  size: number
): void {
  const sprite = STRUCTURE_SPRITES[structureId];
  if (!sprite) return;

  const gridSize = sprite.length;
  const pixelSize = size / gridSize;

  for (let row = 0; row < gridSize; row++) {
    const rowData = sprite[row];
    if (!rowData) continue;

    for (let col = 0; col < rowData.length; col++) {
      const color = rowData[col];
      if (color) {
        ctx.fillStyle = color;
        ctx.fillRect(
          x + col * pixelSize,
          y + row * pixelSize,
          Math.ceil(pixelSize),
          Math.ceil(pixelSize)
        );
      }
    }
  }
}

/**
 * Get sprite dimensions
 */
export function getSpriteDimensions(structureId: string): { width: number; height: number } {
  const sprite = STRUCTURE_SPRITES[structureId];
  if (!sprite || sprite.length === 0) {
    return { width: 16, height: 16 };
  }
  const firstRow = sprite[0];
  return {
    width: firstRow?.length ?? 16,
    height: sprite.length
  };
}
